import React from "react";
import {Box} from "@mui/material";
import type {GridColDef} from "@mui/x-data-grid-premium";
import {DataGridPremium, useGridApiRef} from "@mui/x-data-grid-premium";
import type {Opportunity} from "../opportunityIntelligenceDemoData";
import {STAGE_LABEL, STAGE_ORDER} from "../opportunityUtils";
import {ConfidenceCell, IdentifiedCell, OppTitleCell, SourceChip, StagePill} from "./OpportunityCells";

export type OpportunityGridApiRef = ReturnType<typeof useGridApiRef>;

type Props = {
    rows: Opportunity[];
    apiRef: OpportunityGridApiRef;
};

const STAGE_OPTIONS = (Object.keys(STAGE_LABEL) as Array<keyof typeof STAGE_LABEL>)
    .map(value => ({value, label: STAGE_LABEL[value]}));

export const OpportunityTable: React.FC<Props> = ({rows, apiRef}) => {
    const columns = React.useMemo<GridColDef<Opportunity>[]>(() => [
        {
            field: 'title',
            headerName: 'Opportunity',
            flex: 2.4,
            minWidth: 320,
            renderCell: ({row}) => <OppTitleCell opp={row}/>,
        },
        {
            field: 'source',
            headerName: 'Source',
            flex: 1.2,
            minWidth: 200,
            valueGetter: (_value, row) => row.source.label,
            renderCell: ({row}) => <SourceChip source={row.source}/>,
        },
        {
            field: 'stage',
            headerName: 'Stage',
            type: 'singleSelect',
            valueOptions: STAGE_OPTIONS,
            flex: 0.9,
            minWidth: 140,
            sortComparator: (a, b) => STAGE_ORDER[a as keyof typeof STAGE_ORDER] - STAGE_ORDER[b as keyof typeof STAGE_ORDER],
            renderCell: ({row}) => <StagePill stage={row.stage}/>,
        },
        {
            field: 'confidence',
            headerName: 'Confidence',
            type: 'number',
            flex: 0.9,
            minWidth: 150,
            align: 'left',
            headerAlign: 'left',
            renderCell: ({row}) => <ConfidenceCell confidence={row.confidence}/>,
        },
        {
            field: 'owner',
            headerName: 'Owner',
            flex: 1,
            minWidth: 150,
        },
        {
            field: 'identifiedValue',
            headerName: 'Identified',
            type: 'number',
            flex: 0.8,
            minWidth: 130,
            renderCell: ({row}) => <IdentifiedCell opp={row}/>,
        },
    ], []);

    return (
        <Box sx={{width: '100%'}}>
            <DataGridPremium
                className="base-table-muix auto-row-height-centered"
                apiRef={apiRef}
                rows={rows}
                columns={columns}
                getRowId={row => row.id}
                getRowHeight={() => 'auto'}
                headerFilters
                disableRowSelectionOnClick
                disableAggregation
                disableRowGrouping
                pagination
                pageSizeOptions={[10, 25, 50]}
                initialState={{
                    pagination: {paginationModel: {pageSize: 10, page: 0}},
                    sorting: {sortModel: [{field: 'identifiedValue', sort: 'desc'}]},
                }}
                sx={{
                    border: 'none',
                    '--rowBorderColor': 'rgba(0,0,0,0.08)',
                    '& .MuiDataGrid-columnHeaderTitle': {fontWeight: 700},
                }}
            />
        </Box>
    );
};
